import React from 'react';
import App from 'next/app';
import ReactGA from 'react-ga';
import NProgress from 'nprogress';
import { hotjar } from 'react-hotjar';
import { Offline } from 'react-detect-offline';
import { createGlobalStyle, css } from 'styled-components';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { animateScroll } from 'react-scroll';

import 'isomorphic-unfetch';

import { ClientContextProvider } from 'react-fetching-library';
import { parseCookies } from 'nookies';
import idx from 'idx';

import config from '../modules/common/config/index';
import HeadShared from '../modules/common/HeadShared';

import { appWithTranslation, useTranslation, Router } from '../modules/common/libs/i18n';
import sentry from '../modules/common/libs/sentry';
import SentryProvider from '../modules/common/libs/sentry/SentryProvider';
import ErrorClientSidePage from '../modules/common/components/errors/ErrorClientSidePage';

import { createClient } from '../modules/common/libs/react-fetching-library/client';
import wrapAuth from '../modules/user/utils/auth/wrapAuth';
import ThemeProvider from '../modules/common/utils/styles/theme/ThemeProvider';
import SimpleBanner from '../modules/common/components/basic/SimpleBanner';
import ApolloProvider from '../modules/common/libs/react-apollo/ApolloProvider';

import { K_USER_TOKEN_COOKIE_KEY } from '../modules/user/utils/auth/AuthProvider';
import '../modules/common/utils/polyfill/google-translate-bug';
import ToastifyContainer from '../modules/common/libs/react-toastify/ToastifyContainer';
import TrackingProvider from '../modules/common/utils/tracking/TrackingProvider';

const { Sentry, captureException } = sentry();

export const SentryClient = Sentry;

// Call it once in your app. At the root of your app is the best place
toast.configure({
  autoClose: 8000
});

const OfflineStyle = createGlobalStyle`
  html {
    padding-top: 50px;
    filter: grayscale(100%);
  }
`;

const OfflineBanner = props => {
  const { t } = useTranslation();
  return (
    <div
      css={css`
        left: 0;
        right: 0;
        top: 0;
        position: fixed;
        z-index: 10;
      `}
    >
      <SimpleBanner>
        <b>{t('general.offline.banner', `You're offline. Please connect to the internet`)}</b>
      </SimpleBanner>
    </div>
  );
};

class MyApp extends App {
  static async getInitialProps({ Component: _Component, ctx }) {
    const cookies = parseCookies(ctx); // universal cookies getter
    const token = idx(cookies, _ => _[K_USER_TOKEN_COOKIE_KEY]);
    const clientConfig = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    const client = createClient(clientConfig);
    ctx.client = client;
    ctx.token = token;

    let pageProps = {};

    const Component = wrapAuth(_Component);
    if (Component.getInitialProps) {
      pageProps = await Component.getInitialProps(ctx);
    }

    return {
      cacheItems: client.cache.getItems(),
      pageProps
    };
  }

  componentDidMount() {
    ReactGA.initialize(config.gaTrackingId);
    ReactGA.pageview(window.location.pathname + window.location.search);

    // Workaround for enzyme
    if (Router.events) {
      Router.events.on('routeChangeStart', NProgress.start);
      Router.events.on('routeChangeComplete', NProgress.done);
      Router.events.on('routeChangeComplete', this.scrollToTop);
      Router.events.on('routeChangeError', NProgress.done);
    }
    if (config.environment === 'production' && process.browser) {
      hotjar.initialize(1488885, 6);
    }
  }

  scrollToTop = () => {
    animateScroll.scrollToTop({
      duration: 0
    });
  };

  render() {
    const { i18n, cacheItems, Component: _Component, pageProps } = this.props;
    const client = createClient();
    client.cache.setItems(cacheItems); // set the result to client side
    const Component = wrapAuth(_Component);
    return (
      // Render the normal Next.js page
      <TrackingProvider>
        <ApolloProvider>
          <SentryProvider value={{ Sentry, captureException }}>
            <ThemeProvider>
              <ClientContextProvider client={client}>
                <HeadShared
                  title="RupiahToken - The First Stablecoin Backed By Rupiah Indonesia"
                  desc="Rupiah Token (IDRT) is a crypto asset on the Ethereum Blockchain that gives you access to global crypto exchanges. You can always redeem 1 IDRT for Rp 1, giving it a stable price."
                />
                <Offline>
                  <OfflineStyle />
                  <OfflineBanner />
                </Offline>
                <Component {...pageProps} />
                <ToastifyContainer />
              </ClientContextProvider>
            </ThemeProvider>
          </SentryProvider>
        </ApolloProvider>
      </TrackingProvider>
    );
  }
}

const withError = WrappedComponent => {
  const Comp = class extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        hasError: false,
        errorEventId: undefined
      };
    }

    static async getInitialProps(rest) {
      const { ctx } = rest;
      try {
        return WrappedComponent.getInitialProps(rest);
      } catch (error) {
        // Capture errors that happen during a page's getInitialProps.
        // This will work on both client and server sides.
        const errorEventId = captureException(error, ctx);
        return {
          hasError: true,
          errorEventId
        };
      }
    }

    static getDerivedStateFromProps(props, state) {
      // If there was an error generated within getInitialProps, and we haven't
      // yet seen an error, we add it to this.state here
      return {
        hasError: props.hasError || state.hasError || false,
        errorEventId: props.errorEventId || state.errorEventId || undefined
      };
    }

    static getDerivedStateFromError() {
      // React Error Boundary here allows us to set state flagging the error (and
      // later render a fallback UI).
      return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
      const errorEventId = captureException(error, { errorInfo });

      // Store the event id at this point as we don't have access to it within
      // `getDerivedStateFromError`.
      this.setState({ errorEventId });
    }

    render() {
      const { hasError, errorEventId } = this.state;
      return hasError ? (
        <ErrorClientSidePage
          onReportError={() => Sentry.showReportDialog({ eventId: errorEventId })}
        />
      ) : (
        <WrappedComponent {...this.props} />
      );
    }
  };
  return Comp;
};

export default withError(appWithTranslation(MyApp));
